import { Box, Button } from '@mui/material';
import LogRocket from 'logrocket';
import { type ReactElement, Suspense, useCallback, useMemo } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { type SlabContextType, SlabProvider } from '@core/context/SlabContext';
import { NewUserInfo } from '@core/types/api/UserInfo/UserInfo';
import { hasValue } from '@core/utils/equality';
import { useSlabAuth } from '@web/hooks/useSlabAuth';
import { NotificationPanelProvider } from '@web/providers/NotificationPanelProvider/NotificationPanelProvider';
import { QuickActionsProvider } from '@web/providers/QuickActionsProvider/QuickActionsProvider';

import { AppNavigation } from './components/AppNavigation/AppNavigation';
import { AsyncJobStatusProvider } from './components/AsyncJobStatus/AsyncJobStatusContext';
import { AsyncJobStatusWidget } from './components/AsyncJobStatus/AsyncJobStatusWidget';
import { ErrorFullPage } from './components/Error/ErrorFullPage';
import { LoadingSpinner } from './components/LoadingSpinner/LoadingSpinner';
import { MaintenanceNotice } from './components/MaintenanceNotice/MaintenanceNotice';
import { MobileAppBanner } from './components/MobileAppBanner/MobileAppBanner';
import { PushNotifications } from './components/Notifications/PushNotifications';
import { ServerErrorMessage, useSlabQuery } from './hooks/useSlabQuery';
import { NotFound } from './pages/NotFound/NotFound';
import { TenantAdminDetails } from './pages/TenantsAdmin/TenantAdminDetails/TenantAdminDetails';
import { TenantList } from './pages/TenantsAdmin/TenantAdminList/TenantAdminList';
import { type SlabRoute, UnboundSlabRoutes } from './SlabRoutes';
import { createBoundProxy } from './utils/createBoundProxy';
import { withSlabErrorBoundary } from './utils/withSlabErrorBoundary';

const Loading = (): ReactElement => (
  <LoadingSpinner size={15} unit='rem' sx={{ height: '100vh' }} />
);

// Renders a BrowserRouter to allow usage of the <Link> component to navigate between pages
const UnboundSlabRouter = (): ReactElement | null => {
  // We set `staleTime: Infinity` to avoid re-fetching this data and re-bootstrapping the router and its routes,
  // which was causing problems with closing edit drawers and etc after re-fetching an unchanged copy of this data.
  // We are assuming that the userinfo data won't change during the lifetime of a full-page-view, and/or that if it
  // does change then those changes can wait until the end of the session or will be enforced by the backend well
  // enough to trigger errors where appropriate (even if those errors are not particularly graceful).
  // See https://slabstack.slack.com/archives/C032DAD421L/p1696606903854809 for details.
  const {
    data: userInfo,
    isLoading: isLoadingUserInfo,
    error,
  } = useSlabQuery('GET user info', {}, { staleTime: Infinity, refetchOnMount: 'always' });

  const value = useMemo<SlabContextType>(
    () => ({ userInfo: createBoundProxy(NewUserInfo(userInfo ?? {})) }),
    [userInfo],
  );

  const { logout } = useSlabAuth();
  const logoutHandler = useCallback(() => {
    logout();
  }, [logout]);

  // This check for userInfo being loaded & defined is essential, as it is our first
  // line of defense against rendering the the app for our users.
  // All child components of this router will have access to the userInfo.
  if (isLoadingUserInfo) {
    return <Loading />;
  }

  if (error !== null || userInfo === undefined) {
    LogRocket.error('Error fetching user info while loading SlabRouter', ServerErrorMessage(error));

    return (
      <Box sx={{ margin: 'auto', maxWidth: '20rem' }}>
        <div>An unknown error occurred</div>

        <Button variant='slab-secondary' onClick={logoutHandler}>
          Log Out
        </Button>
      </Box>
    );
  }

  const renderRoutes = (routes: SlabRoute[]): ReactElement[] => {
    return routes.map((route) => {
      let Element = route.main;
      let enableSubroutes = true;
      if (route.obfuscatedMain) {
        if (!userInfo.hasFlags(route.disableObfuscation || [])) {
          Element = route.obfuscatedMain;
          enableSubroutes = false;
        }
      }

      return (
        <Route key={`route-${route.path}`} path={route.path} element={<Element />}>
          {hasValue(route.subRoutes) &&
            enableSubroutes &&
            renderRoutes(route.subRoutes.flatMap((section) => section.children))}
        </Route>
      );
    });
  };

  return (
    <BrowserRouter>
      <SlabProvider context={value}>
        <AsyncJobStatusProvider>
          <PushNotifications />
          <MobileAppBanner />
          <QuickActionsProvider>
            <NotificationPanelProvider>
              <AppNavigation>
                <MaintenanceNotice />
                <Suspense fallback={<Loading />}>
                  <Routes>
                    {UnboundSlabRoutes(userInfo).map((routeSection) =>
                      renderRoutes(routeSection.children),
                    )}

                    <Route path='/_slabstack_staff' element={<TenantList />} />
                    <Route path='/_slabstack_staff/:tenantID' element={<TenantAdminDetails />} />
                    <Route path='*' element={<NotFound />} />
                  </Routes>
                </Suspense>
              </AppNavigation>
            </NotificationPanelProvider>
          </QuickActionsProvider>
          <AsyncJobStatusWidget />
        </AsyncJobStatusProvider>
      </SlabProvider>
    </BrowserRouter>
  );
};

export const SlabRouter = withSlabErrorBoundary(UnboundSlabRouter, {
  FallbackComponent: ErrorFullPage,
});
